import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../api/api';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import {
  ArrowLeft, Warning, Plus, X, Camera, Sparkle, CheckCircle, Wrench,
  HardHat, ArrowClockwise, CaretDown, CaretUp, CurrencyDollar
} from '@phosphor-icons/react';
import { ChangeOrderModal } from '../components/ai-pricing/ChangeOrderModal';

const SEVERITY_META = {
  low: { label: 'Low', cls: 'bg-neutral-200 text-neutral-700' },
  medium: { label: 'Medium', cls: 'bg-yellow-100 text-yellow-800' },
  high: { label: 'High', cls: 'bg-orange-100 text-orange-800' },
  safety: { label: 'Safety', cls: 'bg-red-600 text-white' },
};
const STATUS_META = {
  open: { label: 'Open', cls: 'bg-red-100 text-red-700' },
  in_progress: { label: 'In Progress', cls: 'bg-blue-100 text-blue-700' },
  resolved: { label: 'Resolved', cls: 'bg-green-100 text-green-700' },
};

const compressImage = (file) => new Promise((resolve) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    const img = new Image();
    img.onerror = () => resolve(null);
    img.onload = () => {
      const max = 1280;
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.75));
    };
    img.src = e.target.result;
  };
  reader.readAsDataURL(file);
});

const AISuggestion = ({ problem, onRefresh }) => {
  const [busy, setBusy] = useState(false);
  const s = problem.ai_suggestion;
  const refresh = async () => {
    setBusy(true);
    try { await onRefresh(problem.id); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 bg-[#2B4C6F]/5 dark:bg-[#2B4C6F]/20 border border-[#2B4C6F]/30 p-3" data-testid={`ai-suggestion-${problem.id}`}>
      <div className="flex items-center justify-between mb-1">
        <p className="text-[10px] font-black uppercase tracking-widest text-[#2B4C6F] dark:text-[#7BA3CC] flex items-center gap-1">
          <Sparkle size={12} weight="fill" /> AI Field Advice
        </p>
        <button onClick={refresh} disabled={busy} className="text-[#2B4C6F] dark:text-[#7BA3CC]" title="Regenerate" data-testid={`refresh-ai-${problem.id}`}>
          <ArrowClockwise size={14} className={busy ? 'animate-spin' : ''} />
        </button>
      </div>
      {!s && problem.ai_error && <p className="text-xs text-red-600 dark:text-red-400">{problem.ai_error}</p>}
      {!s && !problem.ai_error && <p className="text-xs text-neutral-500 italic">{busy ? 'Analyzing…' : 'AI is analyzing this problem — it will appear here shortly.'}</p>}
      {s && (
        <div className="text-xs space-y-1.5 text-neutral-700 dark:text-neutral-300">
          <p><span className="font-bold">Likely cause:</span> {s.likely_cause}</p>
          {s.fix_steps?.length > 0 && (
            <ol className="list-decimal ml-4 space-y-0.5">
              {s.fix_steps.map((f, i) => <li key={i}>{f}</li>)}
            </ol>
          )}
          {s.safety_flags?.length > 0 && (
            <p className="text-red-700 dark:text-red-400 font-bold">⚠ {s.safety_flags.join(' · ')}</p>
          )}
          {s.est_repair_cost && <p className="font-mono font-bold">Est. repair: {s.est_repair_cost}</p>}
        </div>
      )}
    </div>
  );
};

export default function SiteProblems() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isContractor = user.user_type === 'contractor';
  const [problems, setProblems] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [form, setForm] = useState({ job_id: '', title: '', description: '', severity: 'medium' });
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [workersByJob, setWorkersByJob] = useState({});
  const [resolveId, setResolveId] = useState(null);
  const [resolveNote, setResolveNote] = useState('');
  const [expanded, setExpanded] = useState({});
  const [changeOrderCtx, setChangeOrderCtx] = useState(null);

  const load = useCallback(async () => {
    try {
      const [pr, jr] = await Promise.all([
        axiosInstance.get('/site-problems/mine'),
        axiosInstance.get('/site-problems/reportable-jobs'),
      ]);
      setProblems(pr.data.problems || []);
      setJobs(jr.data.jobs || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll briefly while any problem is still waiting on its AI suggestion
  useEffect(() => {
    const waiting = problems.some((p) => !p.ai_suggestion && !p.ai_error && p.status !== 'resolved');
    if (!waiting) return;
    let count = 0;
    const t = setInterval(async () => {
      count += 1;
      if (count > 8) { clearInterval(t); return; }
      try {
        const r = await axiosInstance.get('/site-problems/mine');
        setProblems(r.data.problems || []);
      } catch (e) { clearInterval(t); }
    }, 5000);
    return () => clearInterval(t);
  }, [problems]);

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 4 - photos.length);
    const compressed = await Promise.all(files.map(compressImage));
    const good = compressed.filter(Boolean);
    if (good.length < compressed.length) {
      toast({ title: 'Some photos were skipped', description: 'Unsupported image format (try JPG/PNG).', variant: 'destructive' });
    }
    setPhotos((prev) => [...prev, ...good].slice(0, 4));
  };

  const submitReport = async () => {
    if (!form.job_id || form.title.trim().length < 3 || form.description.trim().length < 5) {
      toast({ title: 'Pick a job and fill in title + description', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await axiosInstance.post('/site-problems', { ...form, photos });
      toast({ title: 'Problem reported', description: 'AI is analyzing it now.' });
      setShowReport(false);
      setForm({ job_id: '', title: '', description: '', severity: 'medium' });
      setPhotos([]);
      load();
    } catch (e) {
      toast({ title: 'Report failed', description: e.response?.data?.detail, variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  const patch = async (id, body, okMsg) => {
    try {
      await axiosInstance.patch(`/site-problems/${id}`, body);
      if (okMsg) toast({ title: okMsg });
      load();
    } catch (e) {
      toast({ title: 'Update failed', description: e.response?.data?.detail, variant: 'destructive' });
    }
  };

  const loadWorkers = async (jobId) => {
    if (workersByJob[jobId]) return;
    try {
      const r = await axiosInstance.get(`/site-problems/job/${jobId}/workers`);
      setWorkersByJob((prev) => ({ ...prev, [jobId]: r.data.workers || [] }));
    } catch (e) { /* noop */ }
  };

  const refreshAI = async (id) => {
    try {
      const r = await axiosInstance.post(`/site-problems/${id}/ai-suggest`);
      setProblems((prev) => prev.map((p) => p.id === id ? { ...p, ai_suggestion: r.data.ai_suggestion } : p));
    } catch (e) {
      toast({ title: 'AI suggestion failed', variant: 'destructive' });
    }
  };

  const visible = problems.filter((p) => filter === 'all' || p.status === filter);

  return (
    <div className="min-h-screen bg-[#F0F0F2] dark:bg-neutral-900 pb-24" data-testid="site-problems-page">
      <div className="bg-[#0F172A] border-b-4 border-[#D95800]">
        <div className="max-w-3xl mx-auto px-4 py-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => navigate(-1)} className="text-white/70 hover:text-white" data-testid="back-btn"><ArrowLeft size={22} /></button>
            <div className="w-10 h-10 bg-[#D95800] border-2 border-white flex items-center justify-center shrink-0">
              <Warning size={22} weight="fill" className="text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-black uppercase tracking-tight text-white truncate">Site Problems</h1>
              <p className="text-xs text-neutral-400 font-mono">Report · Assign · Resolve</p>
            </div>
          </div>
          <Button
            onClick={() => setShowReport(true)}
            className="h-10 bg-[#D95800] hover:bg-[#B84A00] text-white font-black uppercase text-xs border-2 border-white rounded-none shrink-0"
            data-testid="report-problem-btn"
          >
            <Plus size={16} weight="bold" className="mr-1" /> Report
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex gap-2 mb-5 overflow-x-auto">
          {['all', 'open', 'in_progress', 'resolved'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-xs font-black uppercase tracking-wider border-2 border-black whitespace-nowrap ${filter === f ? 'bg-[#2B4C6F] text-white' : 'bg-white dark:bg-neutral-800 dark:text-white dark:border-white'}`}
              data-testid={`filter-${f}`}
            >
              {f === 'all' ? 'All' : STATUS_META[f].label}
            </button>
          ))}
        </div>

        {loading && <div className="space-y-4">{[0, 1].map((i) => <div key={i} className="h-36 bg-white dark:bg-neutral-800 border-2 border-neutral-300 animate-pulse" />)}</div>}

        {!loading && visible.length === 0 && (
          <div className="text-center py-16">
            <HardHat size={56} weight="light" className="text-neutral-300 mx-auto mb-4" />
            <p className="text-neutral-500 font-bold">No {filter !== 'all' ? STATUS_META[filter]?.label.toLowerCase() : ''} problems</p>
            <p className="text-xs text-neutral-400 mt-1">Spot something wrong on site? Hit Report.</p>
          </div>
        )}

        <div className="space-y-4">
          {visible.map((p) => {
            const sev = SEVERITY_META[p.severity] || SEVERITY_META.medium;
            const st = STATUS_META[p.status] || STATUS_META.open;
            const isOpen = expanded[p.id];
            return (
              <div key={p.id} className="bg-white dark:bg-[#0F172A] border-2 border-black dark:border-white shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]" data-testid={`problem-card-${p.id}`}>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 ${sev.cls}`} data-testid={`severity-${p.id}`}>{sev.label}</span>
                        <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 ${st.cls}`} data-testid={`status-${p.id}`}>{st.label}</span>
                      </div>
                      <h3 className="font-black text-neutral-900 dark:text-white">{p.title}</h3>
                      <p className="text-xs text-neutral-500">{p.job_title} · by {p.reported_by_name}{p.assigned_to_name ? ` · assigned to ${p.assigned_to_name}` : ''}</p>
                    </div>
                    <button onClick={() => { const open = !isOpen; setExpanded((e) => ({ ...e, [p.id]: open })); if (open && isContractor) loadWorkers(p.job_id); }} className="text-neutral-400 shrink-0" data-testid={`expand-${p.id}`}>
                      {isOpen ? <CaretUp size={18} /> : <CaretDown size={18} />}
                    </button>
                  </div>

                  {isOpen && (
                    <div className="mt-3">
                      <p className="text-sm text-neutral-700 dark:text-neutral-300 whitespace-pre-wrap">{p.description}</p>
                      {p.photos?.length > 0 && (
                        <div className="flex gap-2 mt-3 overflow-x-auto">
                          {p.photos.map((ph, i) => (
                            <img key={i} src={ph} alt={`problem ${i + 1}`} className="w-24 h-24 object-cover border-2 border-black shrink-0" />
                          ))}
                        </div>
                      )}
                      <AISuggestion problem={p} onRefresh={refreshAI} />
                      {p.resolution_note && (
                        <div className="mt-3 bg-green-50 dark:bg-green-900/20 border border-green-300 p-3">
                          <p className="text-[10px] font-black uppercase tracking-widest text-green-700 dark:text-green-400 mb-1 flex items-center gap-1"><CheckCircle size={12} weight="fill" /> Resolution</p>
                          <p className="text-xs text-neutral-700 dark:text-neutral-300">{p.resolution_note}</p>
                          {p.resolution_photos?.length > 0 && (
                            <div className="flex gap-2 mt-2">
                              {p.resolution_photos.map((ph, i) => <img key={i} src={ph} alt="fix" className="w-16 h-16 object-cover border border-black" />)}
                            </div>
                          )}
                        </div>
                      )}

                      {p.status !== 'resolved' && (
                        <div className="flex flex-wrap gap-2 mt-4">
                          {p.status === 'open' && (
                            <Button onClick={() => patch(p.id, { status: 'in_progress' }, 'Marked in progress')} className="h-8 text-xs font-bold uppercase bg-blue-600 hover:bg-blue-700 text-white rounded-none border-2 border-black" data-testid={`start-btn-${p.id}`}>
                              <Wrench size={14} className="mr-1" /> Start Work
                            </Button>
                          )}
                          <Button onClick={() => { setResolveId(p.id); setResolveNote(''); }} className="h-8 text-xs font-bold uppercase bg-green-600 hover:bg-green-700 text-white rounded-none border-2 border-black" data-testid={`resolve-btn-${p.id}`}>
                            <CheckCircle size={14} className="mr-1" /> Resolve
                          </Button>
                          {isContractor && (
                            <Button
                              onClick={() => setChangeOrderCtx({
                                job_type: p.job_title || '',
                                location: '',
                                original_total: null,
                                original_scope: null,
                                source: 'site_problem',
                                source_id: p.id,
                                prefill: `Repair needed on "${p.job_title}": ${p.title}. ${p.description}${p.ai_suggestion?.likely_cause ? ` Likely cause: ${p.ai_suggestion.likely_cause}` : ''}`,
                              })}
                              className="h-8 text-xs font-bold uppercase bg-[#2B4C6F] hover:bg-[#1F3A57] text-white rounded-none border-2 border-black"
                              data-testid={`price-fix-btn-${p.id}`}
                            >
                              <CurrencyDollar size={14} className="mr-1" /> Price This Fix
                            </Button>
                          )}
                          {isContractor && (
                            <select
                              className="h-8 text-xs font-bold border-2 border-black px-2 bg-white dark:bg-neutral-800 dark:text-white dark:border-white"
                              value={p.assigned_to || ''}
                              onFocus={() => loadWorkers(p.job_id)}
                              onChange={(e) => patch(p.id, { assigned_to: e.target.value }, e.target.value ? 'Assigned' : 'Unassigned')}
                              data-testid={`assign-select-${p.id}`}
                            >
                              <option value="">Assign to…</option>
                              {p.assigned_to && !(workersByJob[p.job_id] || []).some((w) => w.id === p.assigned_to) && (
                                <option value={p.assigned_to}>{p.assigned_to_name}</option>
                              )}
                              {(workersByJob[p.job_id] || []).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                            </select>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Report modal */}
      {showReport && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center" data-testid="report-problem-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowReport(false)} />
          <div className="relative w-full sm:max-w-lg bg-white dark:bg-[#0F172A] border-2 border-black dark:border-white max-h-[92vh] overflow-y-auto">
            <div className="sticky top-0 p-4 bg-[#D95800] text-white flex items-center justify-between border-b-2 border-black">
              <h3 className="font-black uppercase tracking-wide flex items-center gap-2"><Warning size={18} weight="fill" /> Report a Problem</h3>
              <button onClick={() => setShowReport(false)} data-testid="close-report-modal"><X size={20} /></button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="text-xs font-black uppercase tracking-wider text-neutral-500">Job *</label>
                <select
                  className="w-full h-11 border-2 border-black dark:border-white px-2 bg-white dark:bg-neutral-800 dark:text-white mt-1"
                  value={form.job_id}
                  onChange={(e) => setForm({ ...form, job_id: e.target.value })}
                  data-testid="report-job-select"
                >
                  <option value="">Select job…</option>
                  {jobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-black uppercase tracking-wider text-neutral-500">What's wrong? *</label>
                <input
                  className="w-full h-11 border-2 border-black dark:border-white px-3 bg-white dark:bg-neutral-800 dark:text-white mt-1"
                  placeholder="e.g. Water leak behind drywall"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="report-title-input"
                />
              </div>
              <div>
                <label className="text-xs font-black uppercase tracking-wider text-neutral-500">Details *</label>
                <textarea
                  className="w-full border-2 border-black dark:border-white p-3 bg-white dark:bg-neutral-800 dark:text-white mt-1 min-h-[90px]"
                  placeholder="Where is it, what happened, what's blocked…"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="report-description-input"
                />
              </div>
              <div>
                <label className="text-xs font-black uppercase tracking-wider text-neutral-500">Severity</label>
                <div className="flex gap-2 mt-1">
                  {Object.entries(SEVERITY_META).map(([k, m]) => (
                    <button
                      key={k}
                      onClick={() => setForm({ ...form, severity: k })}
                      className={`px-3 py-1.5 text-xs font-black uppercase border-2 ${form.severity === k ? 'border-black ' + m.cls : 'border-neutral-300 text-neutral-400 bg-white dark:bg-neutral-800'}`}
                      data-testid={`severity-chip-${k}`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-black uppercase tracking-wider text-neutral-500">Photos (up to 4)</label>
                <div className="flex gap-2 mt-1 flex-wrap">
                  {photos.map((ph, i) => (
                    <div key={i} className="relative">
                      <img src={ph} alt="" className="w-20 h-20 object-cover border-2 border-black" />
                      <button onClick={() => setPhotos(photos.filter((_, x) => x !== i))} className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs" data-testid={`remove-photo-${i}`}>×</button>
                    </div>
                  ))}
                  {photos.length < 4 && (
                    <label className="w-20 h-20 border-2 border-dashed border-neutral-400 flex flex-col items-center justify-center cursor-pointer text-neutral-400" data-testid="add-photo-label">
                      <Camera size={22} />
                      <span className="text-[9px] font-bold uppercase">Add</span>
                      <input type="file" accept="image/*" multiple capture="environment" className="hidden" onChange={addPhotos} data-testid="photo-input" />
                    </label>
                  )}
                </div>
              </div>
              <Button
                onClick={submitReport}
                disabled={submitting}
                className="w-full h-12 bg-[#D95800] hover:bg-[#B84A00] text-white font-black uppercase border-2 border-black rounded-none shadow-[3px_3px_0px_#000]"
                data-testid="submit-report-btn"
              >
                {submitting ? 'Reporting…' : 'Report Problem'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Change order from a problem */}
      <ChangeOrderModal
        isOpen={!!changeOrderCtx}
        onClose={() => setChangeOrderCtx(null)}
        context={changeOrderCtx}
      />

      {/* Resolve modal */}
      {resolveId && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center" data-testid="resolve-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => setResolveId(null)} />
          <div className="relative w-full sm:max-w-md bg-white dark:bg-[#0F172A] border-2 border-black dark:border-white p-4">
            <h3 className="font-black uppercase tracking-wide text-neutral-900 dark:text-white mb-3 flex items-center gap-2">
              <CheckCircle size={18} weight="fill" className="text-green-600" /> Mark Resolved
            </h3>
            <textarea
              className="w-full border-2 border-black dark:border-white p-3 bg-white dark:bg-neutral-800 dark:text-white min-h-[90px]"
              placeholder="How was it fixed?"
              value={resolveNote}
              onChange={(e) => setResolveNote(e.target.value)}
              data-testid="resolve-note-input"
            />
            <div className="flex gap-2 mt-3">
              <Button onClick={() => setResolveId(null)} variant="outline" className="flex-1 h-10 font-bold uppercase border-2 border-black rounded-none" data-testid="cancel-resolve-btn">Cancel</Button>
              <Button
                onClick={() => { patch(resolveId, { status: 'resolved', resolution_note: resolveNote }, 'Problem resolved'); setResolveId(null); }}
                className="flex-1 h-10 bg-green-600 hover:bg-green-700 text-white font-black uppercase border-2 border-black rounded-none"
                data-testid="confirm-resolve-btn"
              >
                Resolve
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
